import { Space, Typography, Card, Button, Input, Grid } from "antd";
import {
  EditOutlined,
  UploadOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { CustomButtonComponent } from "../global/CustomButtonComponent";
import { FmvTableComponent, FmvRecord } from "./FmvTableComponent";
import { FmvUpdateModal } from "./FmvUpdateModal";
import { FmvBulkUploadModal } from "./FmvBulkUploadModal";
import type { BulkUpsertResponse, VehiclesMeta } from "../../helpers/interfaces/VehiclesInterfaces";

const { Text } = Typography;

type FmvIndexProps = {
  data: FmvRecord[];
  loading: boolean;
  meta: VehiclesMeta | null;
  onPageChange: (page: number, pageSize: number) => void;
  selectedRow: FmvRecord | null;
  selectedRowKey: string | null;
  onSelectRow: (record: FmvRecord | null) => void;
  searchText: string;
  onSearchChange: (value: string) => void;
  // Update modal
  updateModalOpen: boolean;
  onOpenUpdateModal: () => void;
  onCloseUpdateModal: () => void;
  onSaveUpdate: (record: FmvRecord) => void;
  updateLoading: boolean;
  // Bulk upload modal
  bulkUploadModalOpen: boolean;
  onOpenBulkUploadModal: () => void;
  onCloseBulkUploadModal: () => void;
  onConfirmBulkUpload: (records: FmvRecord[]) => Promise<BulkUpsertResponse | null>;
  bulkUploadLoading: boolean;
  onRefresh: () => void;
};

export const FmvIndexComponent = ({
  data,
  loading,
  meta,
  onPageChange,
  selectedRow,
  selectedRowKey,
  onSelectRow,
  searchText,
  onSearchChange,
  updateModalOpen,
  onOpenUpdateModal,
  onCloseUpdateModal,
  onSaveUpdate,
  updateLoading,
  bulkUploadModalOpen,
  onOpenBulkUploadModal,
  onCloseBulkUploadModal,
  onConfirmBulkUpload,
  bulkUploadLoading,
  onRefresh,
}: FmvIndexProps) => {
  const screens = Grid.useBreakpoint();

  return (
    <div style={{ margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "12px",
        }}
        bodyStyle={{ padding: "24px" }}
      >
        {/* Helper text for admin */}
        <div
          style={{
            background: "#f1f5f9",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <QuestionCircleOutlined style={{ color: "#1e293b", fontSize: "18px", marginTop: "2px" }} />
          <div>
            <Text strong style={{ display: "block", marginBottom: "4px" }}>Admin Guide</Text>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              Filter by brand/model/year, sort by pricing, and compare historical price progression in the expanded rows.
            </Text>
          </div>
        </div>

        {/* Action buttons & Search */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Space size={12} direction={screens.xs ? "vertical" : "horizontal"} style={{ width: screens.xs ? "100%" : "auto" }}>
            <Input.Search
              placeholder="Search FMV data..."
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: screens.xs ? "100%" : 350 }}
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              onSearch={onSearchChange}
            />
            {selectedRow && (
              <CustomButtonComponent
                variant="secondary"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => onSelectRow(null)}
                style={{ width: screens.xs ? "100%" : "auto" }}
              >
                Clear Selection
              </CustomButtonComponent>
            )}
            <CustomButtonComponent
              variant="secondary"
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              style={{ width: screens.xs ? "100%" : "auto" }}
            >
              Refresh
            </CustomButtonComponent>
          </Space>

          <Space size={12} direction={screens.xs ? "vertical" : "horizontal"} style={{ width: screens.xs ? "100%" : "auto", marginTop: screens.xs ? 8 : 0 }}>
            {selectedRow && (
              <CustomButtonComponent
                variant="secondary"
                icon={<EditOutlined />}
                onClick={onOpenUpdateModal}
                style={{ width: screens.xs ? "100%" : "auto" }}
              >
                Update Entry
              </CustomButtonComponent>
            )}
            
            <CustomButtonComponent
              variant="primary"
              icon={<UploadOutlined />}
              onClick={onOpenBulkUploadModal}
              style={{ width: screens.xs ? "100%" : "auto" }}
            >
              Bulk Upload CSV
            </CustomButtonComponent>
          </Space>
        </div>

        <style>{`
          .premium-search {
            box-shadow: none !important;
          }

          .premium-search .ant-input-wrapper.ant-input-group {
            border: 1px solid #d6dee8 !important;
            border-radius: 8px !important;
            overflow: hidden !important;
            box-shadow: none !important;
          }
          
          .premium-search .ant-input {
            border: none !important;
            box-shadow: none !important;
          }

          .premium-search .ant-input-group-addon {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
            box-shadow: none !important;
          }

          .premium-search .ant-input-search-button {
            border: none !important;
            border-left: 1px solid #d6dee8 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            height: 100% !important;
          }
          
          .premium-search .ant-input-search-button:hover {
            background: #f1f5f9 !important;
            color: #1e293b !important;
            box-shadow: none !important;
          }

          .premium-search .ant-input-search-button:focus {
            box-shadow: none !important;
          }
        `}</style>

        {/* Table */}
        <FmvTableComponent
          data={data}
          loading={loading}
          meta={meta}
          onPageChange={onPageChange}
          onSelectRow={onSelectRow}
          selectedRowKey={selectedRowKey}
        />
      </Card>

      {/* Modals */}
      <FmvUpdateModal
        open={updateModalOpen}
        record={selectedRow}
        onCancel={onCloseUpdateModal}
        onSave={onSaveUpdate}
        loading={updateLoading}
      />

      <FmvBulkUploadModal
        open={bulkUploadModalOpen}
        onCancel={onCloseBulkUploadModal}
        onConfirmUpload={onConfirmBulkUpload}
        loading={bulkUploadLoading}
      />
    </div>
  );
};
